// SaveToCalendarButton.tsx
// Component for saving/removing events to "My MISO Calendar"

import React from 'react';
import { getEventStatus, getCommitteeStatus } from './CalendarStatusBatcher';
import { Toast } from '../../toast';

interface ISaveToCalendarButtonProps {
    eventContentGuid: string;
    eventEntityId?: number;
    eventEntityName?: string;
    eventCanceled: boolean;
    loggedIn: boolean;
    currentUrl?: string;
    compact?: boolean;
    rightBorder?: boolean;
    hideMyMisoCalendar?: boolean;
}

interface ISaveToCalendarButtonState {
    isSaved: boolean;
    isCommitteeSaved: boolean;
    isLoading: boolean;
    isExcluding: boolean;
    isUnsubscribing: boolean;
    isExcluded: boolean;
}

export class SaveToCalendarButton extends React.Component<ISaveToCalendarButtonProps, ISaveToCalendarButtonState> {
    constructor(props: ISaveToCalendarButtonProps) {
        super(props);
        this.state = {
            isSaved: false,
            isCommitteeSaved: false,
            isLoading: true,
            isExcluding: false,
            isUnsubscribing: false,
            isExcluded: false
        };
    }

    private static PENDING_SAVE_KEY = 'pendingSaveEvent';
    private _isMounted = false;

    public componentDidMount() {
        this._isMounted = true;
        if (this.props.loggedIn && !this.props.eventCanceled) {
            var pendingGuid = this.getPendingSave();
            if (pendingGuid && pendingGuid === this.props.eventContentGuid) {
                this.clearPendingSave();
                this.saveEvent();
            } else {
                this.checkIfSaved();
            }
        } else {
            this.setState({ isLoading: false });
        }
        window.addEventListener('committeeSubscriptionChanged', this.handleCommitteeSubscriptionChanged as EventListener);
    }

    public componentWillUnmount() {
        this._isMounted = false;
        window.removeEventListener('committeeSubscriptionChanged', this.handleCommitteeSubscriptionChanged as EventListener);
    }

    private handleCommitteeSubscriptionChanged = (e: CustomEvent<{ committeeId: number; isSubscribed: boolean }>) => {
        if (!this._isMounted) return;
        if (this.props.eventEntityId && e.detail.committeeId === this.props.eventEntityId) {
            this.setState({ isCommitteeSaved: e.detail.isSubscribed });
        }
    }

    private getPendingSave(): string | null {
        if (typeof window === 'undefined' || !window.sessionStorage) return null;
        return sessionStorage.getItem(SaveToCalendarButton.PENDING_SAVE_KEY);
    }

    private setPendingSave(eventContentGuid: string): void {
        if (typeof window !== 'undefined' && window.sessionStorage) {
            sessionStorage.setItem(SaveToCalendarButton.PENDING_SAVE_KEY, eventContentGuid);
        }
    }

    private clearPendingSave(): void {
        if (typeof window !== 'undefined' && window.sessionStorage) {
            sessionStorage.removeItem(SaveToCalendarButton.PENDING_SAVE_KEY);
        }
    }

    private checkIfSaved() {
        // Use batched request for event saved/excluded status
        getEventStatus(this.props.eventContentGuid).then(status => {
            if (!this._isMounted) return;
            this.setState({ isSaved: status.isSaved, isExcluded: status.isExcluded, isLoading: false });
        });

        if (this.props.eventEntityId) {
            getCommitteeStatus(this.props.eventEntityId).then(status => {
                if (!this._isMounted) return;
                this.setState({ isCommitteeSaved: status.isSubscribed, isLoading: false });
            });
        }
    }

    private static LOGIN_TOAST_MESSAGE = "Please login to access 'My MISO Calendar' to add/remove meetings and committee subscriptions. If you do not have a MISO Public Website account, click on 'Create Profile' below to create an account.";

    private getLoginUrl(): string {
        if (this.props.currentUrl) {
            var returnUrl = encodeURIComponent(this.props.currentUrl);
            return `/account/login/?ReturnUrl=${returnUrl}`;
        }
        return '/account/login';
    }

    private handleLoginClick = () => {
        this.setPendingSave(this.props.eventContentGuid);
        Toast.SetPendingToast(SaveToCalendarButton.LOGIN_TOAST_MESSAGE, 'My MISO Calendar', 'info');
    }

    private handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        if (!this.props.loggedIn) {
            // Redirect to login with return URL
            this.setPendingSave(this.props.eventContentGuid);
            Toast.SetPendingToast(SaveToCalendarButton.LOGIN_TOAST_MESSAGE, 'My MISO Calendar', 'info');
            if (typeof window !== 'undefined') {
                window.location.href = this.getLoginUrl();
            }
            return;
        }

        if (this.state.isSaved) {
            this.removeEvent();
        } else {
            this.saveEvent();
        }
    }

    private saveEvent() {
        this.setState({ isLoading: true });

        $.ajax({
            url: "/api/SavedEvents/SaveEvent",
            type: 'POST',
            data: JSON.stringify({ eventContentGuid: this.props.eventContentGuid }),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            success: () => {
                this.setState({ isSaved: true, isLoading: false });
                window.dispatchEvent(new CustomEvent('myMisoCalendarChanged'));
            },
            error: (xhr) => {
                alert('Failed to save meeting: ' + (xhr.responseJSON?.message || 'Unknown error'));
                this.setState({ isLoading: false });
            }
        });
    }

    private removeEvent() {
        this.setState({ isLoading: true });

        $.ajax({
            url: "/api/SavedEvents/RemoveEvent",
            type: 'POST',
            data: JSON.stringify({ eventContentGuid: this.props.eventContentGuid }),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            success: () => {
                this.setState({ isSaved: false, isLoading: false });
                window.dispatchEvent(new CustomEvent('myMisoCalendarChanged'));
            },
            error: (xhr) => {
                alert('Failed to remove meeting: ' + (xhr.responseJSON?.message || 'Unknown error'));
                this.setState({ isLoading: false });
            }
        });
    }

    private handleExcludeEvent = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        this.setState({ isExcluding: true });

        if (this.state.isExcluded) {
            // Remove exclusion (stop excluding)
            $.ajax({
                url: "/api/SavedEvents/RemoveExclusion",
                type: 'POST',
                data: JSON.stringify({ eventContentGuid: this.props.eventContentGuid }),
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                success: () => {
                    this.setState({ isExcluded: false, isExcluding: false });
                    window.dispatchEvent(new CustomEvent('myMisoCalendarChanged'));
                },
                error: (xhr) => {
                    alert('Failed to stop excluding meeting: ' + (xhr.responseJSON?.message || 'Unknown error'));
                    this.setState({ isExcluding: false });
                }
            });
        } else {
            // Exclude event
            $.ajax({
                url: "/api/SavedEvents/ExcludeEvent",
                type: 'POST',
                data: JSON.stringify({ eventContentGuid: this.props.eventContentGuid }),
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                success: () => {
                    this.setState({ isSaved: false, isCommitteeSaved: false, isExcluding: false, isExcluded: true });
                    window.dispatchEvent(new CustomEvent('myMisoCalendarChanged'));
                },
                error: (xhr) => {
                    alert('Failed to exclude meeting: ' + (xhr.responseJSON?.message || 'Unknown error'));
                    this.setState({ isExcluding: false });
                }
            });
        }
    }

    private handleUnsubscribeCommittee = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        
        if (!this.props.eventEntityId) {
            return;
        }

        this.setState({ isUnsubscribing: true });

        $.ajax({
            url: '/api/SubscribedCommittees/Unsubscribe',
            type: 'POST',
            data: JSON.stringify({ committeeId: this.props.eventEntityId }),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            success: () => {
                this.setState({ isCommitteeSaved: false, isUnsubscribing: false });
            },
            error: (xhr) => {
                alert('Failed to unsubscribe from committee: ' + (xhr.responseJSON?.message || 'Unknown error'));
                this.setState({ isUnsubscribing: false });
            }
        });
    }

    private renderCompact() {
        const borderStyle = this.props.rightBorder ? { marginRight: '10px', borderRight: '1px solid grey', paddingRight: '10px' } : {};

        if (this.props.eventCanceled) {
            return (
                <a className=" disabled" style={{ cursor: 'not-allowed', ...borderStyle }}>
                    <i className="fa fa-calendar-plus-o" aria-hidden="true"></i>&nbsp;Add to My MISO Calendar
                </a>
            );
        }

        if (!this.props.loggedIn) {
            return (
                <a className="" href={this.getLoginUrl()} onClick={this.handleLoginClick} style={borderStyle}>
                    <i className="fa fa-calendar-plus-o" aria-hidden="true"></i>&nbsp;Add to My MISO Calendar
                </a>
            );
        }

        if (this.state.isLoading) {
            return (
                <a className=" disabled" style={{ cursor: 'wait', ...borderStyle }}>
                    <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>&nbsp;Loading...
                </a>
            );
        }

        // If committee is subscribed and event is not excluded, show disabled "Added via Committee" button
        if (this.state.isCommitteeSaved && !this.state.isSaved && !this.state.isExcluded) {
            return (
                <a className=" disabled" style={{ cursor: 'default', color: '#5cb85c', ...borderStyle }}>
                    <i className="fa fa-calendar-check-o" aria-hidden="true"></i>&nbsp;Added via Committee
                </a>
            );
        }

        // If event is excluded, show option to stop excluding
        if (this.state.isExcluded) {
            const excludeButtonText = this.state.isExcluding ? 'Removing exclusion...' : 'Stop excluding this meeting';
            return (
                <a 
                    className=""
                    onClick={this.handleExcludeEvent}
                    style={{ cursor: this.state.isExcluding ? 'wait' : 'pointer', ...borderStyle }}
                >
                    <i className={this.state.isExcluding ? "fa fa-spinner fa-spin" : "fa fa-calendar-plus-o"} aria-hidden="true"></i>
                    &nbsp;{excludeButtonText}
                </a>
            );
        }

        const buttonText = this.state.isSaved ? "Remove from My MISO Calendar" : "Add to My MISO Calendar";
        const iconClass = this.state.isSaved ? "fa fa-calendar-minus-o" : "fa fa-calendar-plus-o";

        return (
            <a 
                className=""
                onClick={this.handleClick}
                style={{ cursor: 'pointer', ...borderStyle }}
            >
                <i className={iconClass} aria-hidden="true"></i> {buttonText}
            </a>
        );
    }

    private renderCommitteeSubscribedButtons() {
        const borderStyle = this.props.rightBorder ? { marginRight: '10px', borderRight: '1px solid grey', paddingRight: '10px' } : {};
        const unsubscribeText = this.props.eventEntityName 
            ? `Remove all ${this.props.eventEntityName} meetings from My MISO Calendar`
            : 'Remove all of this committee\'s meetings from My MISO Calendar';
        const excludeButtonText = this.state.isExcluded
            ? (this.state.isExcluding ? 'Removing exclusion...' : 'Stop excluding this meeting')
            : (this.state.isExcluding ? 'Excluding...' : 'Exclude only this meeting');
        const excludeIconClass = this.state.isExcluding 
            ? "fa fa-spinner fa-spin margin-left-10 pull-right" 
            : (this.state.isExcluded ? "fa fa-calendar-plus-o margin-left-10 pull-right" : "fa fa-calendar-minus-o margin-left-10 pull-right");

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '0px 0px 10px 0px', borderBottom: '1px solid #d9d8d6'}}>
                <span style={{ textAlign: 'center' }}>{this.state.isExcluded ? " You have manually excluded this " + this.props.eventEntityName + " meeting from showing in My MISO Calendar." : "You have added all " + this.props.eventEntityName + " meetings to My MISO Calendar."}</span>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', ...borderStyle }}>
                    <a 
                        className={`btn btn-primary-light-blue ${this.state.isExcluding ? 'disabled' : ''}`}
                        onClick={this.handleExcludeEvent}
                        style={{ cursor: this.state.isExcluding ? 'wait' : 'pointer', flex: 1 }}
                    >
                        <span className="buttonText">
                            {excludeButtonText}
                        </span>
                        <i className={excludeIconClass} aria-hidden="true"></i>
                    </a>
                    <a 
                        className={`btn btn-default ${this.state.isUnsubscribing ? 'disabled' : ''}`}
                        onClick={this.handleUnsubscribeCommittee}
                        style={{ cursor: this.state.isUnsubscribing ? 'wait' : 'pointer', flex: 1, border: '2px solid', borderRadius: '4px' }}
                    >
                        <span className="buttonText">
                            {this.state.isUnsubscribing ? 'Removing...' : unsubscribeText}
                        </span>
                        {!this.state.isUnsubscribing && (
                            <i className="fa fa-calendar-times-o margin-left-10 pull-right" aria-hidden="true"></i>
                        )}
                        {this.state.isUnsubscribing && (
                            <i className="fa fa-spinner fa-spin margin-left-10 pull-right" aria-hidden="true"></i>
                        )}
                    </a>
                </div>
            </div>
        );
    }

    public render() {
        // If My MISO Calendar feature is hidden, don't render anything
        if (this.props.hideMyMisoCalendar) {
            return null;
        }

        if (this.props.compact) {
            return this.renderCompact();
        }

        const borderStyle = this.props.rightBorder ? { marginRight: '10px', borderRight: '1px solid grey', paddingRight: '10px' } : {};

        if (this.props.eventCanceled) {
            return (
                <a className="btn btn-primary-light-blue greyOut" style={borderStyle}>
                    <span className="buttonText">Add to My MISO Calendar</span>
                    <i className="fa fa-calendar-plus-o margin-left-30 pull-right" aria-hidden="true"></i>
                </a>
            );
        }

        if (!this.props.loggedIn) {
            return (
                <a className="btn btn-primary-light-blue" href={this.getLoginUrl()} onClick={this.handleLoginClick} style={borderStyle}>
                    <span className="buttonText">Add to My MISO Calendar</span>
                    <i className="fa fa-calendar-plus-o margin-left-30 pull-right" aria-hidden="true"></i>
                </a>
            );
        }

        if (this.state.isLoading) {
            return (
                <a 
                    className="btn btn-primary-light-blue disabled"
                    style={{ cursor: 'wait', ...borderStyle }}
                >
                    <span className="buttonText">Loading...</span>
                    <i className="fa fa-spinner fa-spin margin-left-30 pull-right" aria-hidden="true"></i>
                </a>
            );
        }

        // If committee is subscribed but event is not individually saved, show split buttons
        if (this.state.isCommitteeSaved && !this.state.isSaved) {
            return this.renderCommitteeSubscribedButtons();
        }

        const buttonText = this.state.isSaved ? "Remove from My MISO Calendar" : "Add to My MISO Calendar";
        const iconClass = this.state.isSaved 
            ? "fa fa-calendar-minus-o margin-left-30 pull-right" 
            : "fa fa-calendar-plus-o margin-left-30 pull-right";

        return (
            <a 
                className={`btn btn-primary-light-blue ${this.state.isLoading ? 'disabled' : ''}`}
                onClick={this.handleClick}
                style={{ cursor: this.state.isLoading ? 'wait' : 'pointer', ...borderStyle }}
            >
                <span className="buttonText">
                    {this.state.isLoading ? 'Loading...' : buttonText}
                </span>
                {!this.state.isLoading && (
                    <i className={iconClass} aria-hidden="true"></i>
                )}
                {this.state.isLoading && (
                    <i className="fa fa-spinner fa-spin margin-left-30 pull-right" aria-hidden="true"></i>
                )}
            </a>
        );
    }
}
